import { useEffect, useState } from "react";
import { Clock, Users, Euro, ChevronRight, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, IMAGES } from "@/lib/config";
import useReveal from "@/hooks/useReveal";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function Formations() {
    const ref = useReveal();
    const [formations, setFormations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        profile: "",
        message: "",
    });

    useEffect(() => {
        api.get("/formations")
            .then((r) => setFormations(r.data || []))
            .catch(() => toast.error("Impossible de charger les formations"))
            .finally(() => setLoading(false));
    }, []);

    const openInscription = (f) => {
        setActive(f);
        setForm({ full_name: "", email: "", phone: "", profile: "", message: "" });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!active) return;
        if (!form.full_name || !form.email) {
            toast.error("Nom et email sont requis");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/formations/inscription", {
                ...form,
                formation_id: active.id,
                formation_title: active.title,
            });
            toast.success("Inscription enregistrée — je vous recontacte sous 48h.");
            setActive(null);
        } catch (err) {
            toast.error("Erreur lors de l'envoi. Réessayez.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="formations"
            ref={ref}
            data-testid="formations-section"
            className="relative py-24 md:py-32 bg-ink text-cream overflow-hidden"
        >
            <img
                src={IMAGES.training}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-15"
            />
            <div className="absolute inset-0 bg-ink/60" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="label-overline text-brand">Formations personnalisées</span>
                        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter leading-tight">
                            Apprendre à <span className="italic">comprendre</span> sa maison.
                        </h2>
                    </div>
                    <p className="text-cream/70 max-w-md text-sm md:text-base">
                        Modules courts ou parcours sur-mesure, en présentiel ou sur votre
                        chantier. Pour particuliers, artisans et collectivités.
                    </p>
                </div>

                {loading ? (
                    <div className="text-cream/60" data-testid="formations-loading">
                        Chargement des formations…
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="formations-list">
                        {formations.map((f, idx) => (
                            <article
                                key={f.id}
                                data-testid={`formation-card-${f.id}`}
                                className="reveal hover-lift bg-cream/5 border border-cream/15 backdrop-blur-sm rounded-sm p-7 md:p-9 flex flex-col"
                                style={{ transitionDelay: `${idx * 80}ms` }}
                            >
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-11 h-11 rounded-sm bg-brand/20 border border-brand/40 flex items-center justify-center text-brand">
                                        <GraduationCap size={20} strokeWidth={1.6} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight leading-snug">
                                            {f.title}
                                        </h3>
                                    </div>
                                </div>
                                <p className="text-sm md:text-base text-cream/75 leading-relaxed">
                                    {f.summary}
                                </p>
                                <ul className="mt-5 space-y-2">
                                    {f.topics?.slice(0, 3).map((t) => (
                                        <li key={t} className="text-sm text-cream/70 flex items-start gap-2">
                                            <ChevronRight size={14} className="text-brand mt-1 shrink-0" />
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-7 pt-5 border-t border-cream/10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-cream/70">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock size={14} className="text-brand" /> {f.duration}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Users size={14} className="text-brand" /> {f.audience}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Euro size={14} className="text-brand" /> dès {f.price_from} € HT
                                    </span>
                                </div>
                                <button
                                    onClick={() => openInscription(f)}
                                    data-testid={`formation-inscription-${f.id}`}
                                    className="mt-6 group inline-flex items-center justify-center gap-2 px-5 py-3 bg-brand hover:bg-brand-hover text-cream rounded-sm text-sm font-medium tracking-tight transition-colors w-full md:w-auto"
                                >
                                    S'inscrire
                                    <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                                </button>
                            </article>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent
                    data-testid="inscription-dialog"
                    className="bg-cream text-ink border border-border max-w-lg"
                >
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight font-light">
                            Inscription — {active?.title}
                        </DialogTitle>
                        <DialogDescription className="text-stone2">
                            Je reviens vers vous sous 48h pour confirmer dates et modalités.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Nom complet" required>
                                <Input
                                    data-testid="inscription-name"
                                    value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    placeholder="Jean Dupont"
                                    required
                                />
                            </Field>
                            <Field label="Email" required>
                                <Input
                                    data-testid="inscription-email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="vous@email.fr"
                                    required
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Téléphone">
                                <Input
                                    data-testid="inscription-phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    placeholder="06 12 34 56 78"
                                />
                            </Field>
                            <Field label="Profil">
                                <Select
                                    value={form.profile}
                                    onValueChange={(v) => setForm({ ...form, profile: v })}
                                >
                                    <SelectTrigger data-testid="inscription-profile">
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="particulier">Particulier</SelectItem>
                                        <SelectItem value="artisan">Artisan</SelectItem>
                                        <SelectItem value="professionnel">Professionnel</SelectItem>
                                        <SelectItem value="collectivite">Collectivité</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <Field label="Message (optionnel)">
                            <Textarea
                                data-testid="inscription-message"
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Votre projet, vos disponibilités…"
                            />
                        </Field>
                        <DialogFooter className="pt-2">
                            <button
                                type="button"
                                onClick={() => setActive(null)}
                                className="px-5 py-2.5 border border-border rounded-sm text-sm hover:bg-sand/40"
                                data-testid="inscription-cancel"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="inscription-submit"
                                className="px-5 py-2.5 bg-brand hover:bg-brand-hover disabled:opacity-60 text-cream rounded-sm text-sm font-medium"
                            >
                                {submitting ? "Envoi…" : "Envoyer l'inscription"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-ink/80 uppercase tracking-wider">
                {label} {required && <span className="text-brand">*</span>}
            </Label>
            {children}
        </div>
    );
}
